import { useQuery } from '@tanstack/react-query';
import type { CompatibilityConfig } from 'fuel-benchmark-contracts';
import { Pencil } from 'lucide-react';
import { useMemo, useState } from 'react';
import { orpc } from '../../apis/orpc-query';
import { useAuth } from '../../auth/AuthContext';
import { Button } from '../../components/ui/Button';
import { DataTable, type DataTableColumns } from '../../components/ui/DataTable';
import { InfoCell } from '../../components/ui/InfoCell';
import { StatusText } from '../../components/ui/StatusText';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { catalogueStatusTone, describeCompatibilityDefinition } from './condition-display';
import { CompatibilitySheet } from './CompatibilitySheet';

const PAGE_SIZE = 200;

/** The checks that decide whether two vehicles' runs may be compared at all (plan §6). */
export const CompatibilityTab = () => {
  const { t } = useI18nTranslation();
  const { user } = useAuth();
  const isManager = user?.role === 'MANAGER';

  const [editing, setEditing] = useState<CompatibilityConfig | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const checks = useQuery(
    orpc.config.compatibility.list.queryOptions({ input: { page: 1, pageSize: PAGE_SIZE, sortBy: 'key', sortDirection: 'asc' } }),
  );

  const columns: DataTableColumns<CompatibilityConfig> = useMemo(
    () => [
      {
        accessorKey: 'key',
        header: t('conditions.compatibility.columns.key'),
        cell: ({ row }) => <span className="font-mono text-xs text-foreground">{row.original.key}</span>,
      },
      {
        id: 'protocol',
        accessorFn: (config) => config.protocolCode,
        header: t('conditions.rules.columns.protocol'),
        cell: ({ row }) => (
          <InfoCell
            title={`${row.original.protocolCode} v${row.original.protocolVersion}`}
            subline={row.original.definition.operator}
          />
        ),
      },
      {
        id: 'definition',
        accessorFn: (config) => describeCompatibilityDefinition(config.definition),
        header: t('conditions.compatibility.columns.definition'),
        cell: ({ row }) => <span className="text-foreground">{describeCompatibilityDefinition(row.original.definition)}</span>,
      },
      {
        id: 'onFail',
        accessorFn: (config) => config.definition.onFail,
        header: t('conditions.compatibility.columns.onFail'),
        cell: ({ row }) => (
          <StatusText
            label={t(`enums.onFail.${row.original.definition.onFail}`)}
            // Blocking a comparison outright is the notable outcome; a warning is the softer one.
            tone={row.original.definition.onFail === 'BLOCK' ? 'danger' : 'warning'}
          />
        ),
      },
      {
        accessorKey: 'status',
        header: t('conditions.rules.columns.status'),
        cell: ({ row }) => (
          <StatusText
            label={t(`enums.status.${row.original.status}`)}
            tone={catalogueStatusTone(row.original.status)}
          />
        ),
      },
      {
        id: 'rowActions',
        header: '',
        enableSorting: false,
        cell: ({ row }) =>
          isManager ? (
            <div className="flex justify-end">
              <Button
                variant="ghost"
                size="sm"
                aria-label={t('conditions.compatibility.edit')}
                onClick={() => {
                  setEditing(row.original);
                  setIsSheetOpen(true);
                }}
              >
                <Pencil className="size-3.5" />
              </Button>
            </div>
          ) : null,
      },
    ],
    [isManager, t],
  );

  return (
    <div className="space-y-4">
      <p className="max-w-3xl text-sm text-muted-foreground">
        {t('conditions.compatibility.subtitle')} {isManager ? '' : t('conditions.rules.readOnly')}
      </p>

      {checks.isError ? (
        <p
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
        >
          {t('errors.loadFailed')}
        </p>
      ) : null}

      <DataTable
        columns={columns}
        data={checks.data?.items ?? []}
        isLoading={checks.isPending}
        emptyMessage={t('conditions.compatibility.empty')}
      />

      <CompatibilitySheet
        isOpen={isSheetOpen}
        onClose={() => setIsSheetOpen(false)}
        config={editing}
      />
    </div>
  );
};
